import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, ShoppingBag, Briefcase, ArrowRight, Handshake } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }),
};

export default function AffiliatesPage() {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/affiliates`).then((r) => setAffiliates(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="affiliates-page" className="pt-20 min-h-screen bg-luxury-black">
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" animate="visible" className="mb-16">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.3em] text-gold mb-6">Affiliate Partners</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-5xl sm:text-6xl font-light text-white leading-none mb-6">
              Trusted<br /><span className="gold-gradient-text">Partners</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-white/60 font-body font-light text-base leading-relaxed max-w-2xl">
              Curated services and products from partners who share our commitment to excellence. Each recommendation is personally vetted by our team.
            </motion.p>
          </motion.div>

          {loading ? (
            <div className="text-gold font-body text-sm animate-pulse">Loading partners...</div>
          ) : affiliates.length === 0 ? (
            <motion.div initial="hidden" animate="visible" className="glass-card p-16 text-center">
              <motion.div variants={fadeUp} custom={0}>
                <Handshake size={48} className="text-gold/30 mx-auto mb-4" />
                <h3 className="text-white font-heading text-2xl mb-3">Affiliate Program Coming Soon</h3>
                <p className="text-white/40 font-body text-sm mb-8">Premium partnerships are being curated. Contact us to become an affiliate partner.</p>
                <Link to="/contact" className="inline-flex items-center gap-2 bg-gold text-black font-body font-medium text-sm px-8 py-3 hover:bg-gold-light transition-colors">
                  Contact Us
                </Link>
              </motion.div>
            </motion.div>
          ) : (
            <div className="space-y-12">
              {affiliates.map((affiliate, ai) => (
                <motion.div key={affiliate.id} initial="hidden" whileInView="visible" viewport={{ once: true }} data-testid={`affiliate-${affiliate.slug}`}>
                  <motion.div variants={fadeUp} custom={0} className="flex items-center gap-3 mb-6">
                    <Briefcase size={20} className="text-gold" />
                    <h2 className="text-white font-heading text-2xl">{affiliate.company_name}</h2>
                    {affiliate.description && <span className="text-white/30 text-xs font-body ml-2">— {affiliate.description}</span>}
                  </motion.div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {(affiliate.links || []).map((link, li) => (
                      <motion.a
                        key={link.id}
                        href={`${SITE_URL}/api/partner/click/${affiliate.slug}/${link.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        variants={fadeUp}
                        custom={li}
                        data-testid={`affiliate-link-${link.id}`}
                        className="glass-card p-6 group hover:border-gold/30 transition-all block"
                      >
                        {link.image_url && (
                          <div className="aspect-video mb-4 overflow-hidden bg-luxury-surface">
                            <img src={link.image_url} alt={link.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                          </div>
                        )}
                        <div className="flex items-center gap-2 mb-2">
                          {link.product_type === "merchandise" ? <ShoppingBag size={14} className="text-gold" /> : <Briefcase size={14} className="text-gold" />}
                          <span className="text-gold text-[10px] font-body uppercase tracking-wider">{link.product_type}</span>
                        </div>
                        <h3 className="text-white font-body text-sm font-medium group-hover:text-gold transition-colors mb-1">{link.title}</h3>
                        {link.description && <p className="text-white/40 font-body text-xs leading-relaxed mb-3">{link.description}</p>}
                        <div className="flex items-center justify-between">
                          {link.price && <span className="text-gold font-body font-medium">${link.price.toLocaleString()}</span>}
                          <span className="text-white/30 text-xs font-body group-hover:text-gold transition-colors flex items-center gap-1">
                            View <ExternalLink size={12} />
                          </span>
                        </div>
                      </motion.a>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
